import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type IgnitionSettings = { enabled?: boolean; timeout?: number };
type IgnitionStatus = { enabled?: boolean; V2Active?: boolean; timer?: number };

export default function WebUiSettingsIgnitionPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [settings, setSettings] = React.useState<IgnitionSettings>({});
  const [status, setStatus] = React.useState<IgnitionStatus>({});
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [s, st] = await Promise.all([
      postWebUiApi<IgnitionSettings>({
        service: 'ignition',
        access: 'get',
        command: 'settings',
      }),
      postWebUiApi<IgnitionStatus>({
        service: 'ignition',
        access: 'get',
        command: 'status',
      }),
    ]);
    setSettings(s);
    setStatus(st);
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const statusText = (() => {
    if (!status.enabled) return 'ignition timer: disabled';
    if (status.V2Active) return 'ignition timer: enabled';
    return `ignition timer: ${status.timer ?? 0}s remaining`;
  })();

  return (
    <WebUiPageWrapper titleList={['Settings', 'Ignition']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}

      <div className="flex flex-row gap-2">
        <div className="w-full">
          <h4 className="font-bold">Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Option</th>
                <th>Setting</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Ignition</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    options={[
                      { label: 'enabled', value: 'true' },
                      { label: 'disabled', value: 'false' },
                    ]}
                    disabled={readonly}
                    value={settings.enabled ? 'true' : 'false'}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        enabled: e.target.value === 'true',
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Timeout in seconds</td>
                <td>
                  <WebUiInput
                    type="number"
                    inputSize="sm"
                    min={60}
                    max={86400}
                    disabled={readonly}
                    value={settings.timeout ?? 60}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        timeout: Number(e.target.value),
                      }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <WebUiButton
            className="mt-2"
            disabled={readonly}
            onClick={async () => {
              try {
                await postWebUiApi({
                  service: 'ignition',
                  access: 'set',
                  command: 'settings',
                  parameters: settings,
                });
                setMessage('Ignition settings updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Update Ignition Settings
          </WebUiButton>
        </div>

        <div className="w-full">
          <h4 className="font-bold">Status</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>V2 / Ignition</td>
                <td>{status.V2Active ? 'powered' : 'not powered'}</td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Timer value in seconds</td>
                <td>{status.timer ?? 0}</td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Status</td>
                <td>{statusText}</td>
              </tr>
            </tbody>
          </table>
          <WebUiButton
            className="mt-2"
            onClick={() =>
              reload().catch((err) => setError((err as Error).message))
            }
          >
            Reload
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
